import type { HttpResponse } from '@angular/common/http';
import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import type { DateTime } from 'luxon';
import type { Observable } from 'rxjs';

import type { AssetType } from '#/app/shared/domain/assets/asset.model';
import type { ComparePlansAnnualSpendResponse } from '#/app/shared/domain/plans/compare-plans-annual-spend.model';
import type { ComparePlansConditionDistributionResponse } from '#/app/shared/domain/plans/compare-plans-condition-distribution.model';
import type { ComparePlansConditionOverTimeChartResponse } from '#/app/shared/domain/plans/compare-plans-condition-over-time.model';
import type { ComparePlansFundsPlannedVsNotPlannedResponse } from '#/app/shared/domain/plans/compare-plans-funds-planned.model';
import type { ComparePlansSummaryResponse } from '#/app/shared/domain/plans/compare-plans-summary.model';
import type { ComparePlansSummaryMetricsByAssetTypeResponse } from '#/app/shared/domain/plans/compare-plans-summary-metrics-by-asset-type.model';
import type { ComparePlansVolumeOfRepairsResponse } from '#/app/shared/domain/plans/compare-plans-volume-of-repairs.model';
import type { ConditionsBeforeAfterRepairsResponse } from '#/app/shared/domain/plans/conditions-before-after-repairs.model';
import type {
  ComparePlans,
  CreatePlanRequest,
  PlanResponse,
  UpdatePlanRequest,
} from '#/app/shared/domain/plans/plan.model';
import type { PlanAnnualSpendYearResponse } from '#/app/shared/domain/plans/plan-annual-spend-by-repair-category.model';
import type { PlanAverageConditionsByAssetTypeResponse } from '#/app/shared/domain/plans/plan-average-conditions-by-asset-type.model';
import type { PlanRepairs } from '#/app/shared/domain/plans/plan-repair.model';
import { assertIsDefined } from '#/app/shared/domain/types';
import { DataInvalidationService } from '#/app/shared/services/data-invalidation.service';
import { TenantContext } from '#/app/shared/services/tenant.context';

import type {
  ComparePlansAnnualSpendBreakdownCategory,
  ComparePlansAnnualSpendBreakdownResponse,
} from '../../domain/plans/compare-plans-annual-spend-breakdown.model';

// TODO: Create a separate file for compare-plans related resources and services
@Injectable({
  providedIn: 'root',
})
export class PlansService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  createPlan(request: CreatePlanRequest): Observable<PlanResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.post<PlanResponse>(
      `api/tenants/${tenantId}/plans`,
      request,
    );
  }

  updatePlan(
    planId: string,
    request: UpdatePlanRequest,
  ): Observable<PlanResponse> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.put<PlanResponse>(
      `api/tenants/${tenantId}/plans/${planId}`,
      request,
    );
  }

  deletePlan(planId: string): Observable<never> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.delete<never>(
      `api/tenants/${tenantId}/plans/${planId}`,
    );
  }

  bulkDeletePlans(planIds: string[]) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.delete(
      `api/tenants/${tenantId}/plans/bulk-delete`,
      { body: { planIds } },
    );
  }

  /**
   * Average network condition per pavement asset type, deteriorated to the given plan start date.
   * No request is made until a valid start date is available.
   */
  planAverageConditionsByAssetTypeResource(
    planStartDate: Signal<DateTime | null>,
  ) {
    return httpResource<PlanAverageConditionsByAssetTypeResponse[]>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planStartDateValue = planStartDate();
      const planStartDateIso = planStartDateValue?.isValid
        ? planStartDateValue.toISO()
        : null;

      if (!tenantId || !planStartDateIso) {
        return undefined;
      }

      return `api/tenants/${tenantId}/plans/average-conditions-by-asset-type?planStartDate=${encodeURIComponent(planStartDateIso)}`;
    });
  }

  planResource(planId: Signal<string>) {
    return httpResource<PlanResponse>(() => {
      this.#dataInvalidationService.track('plans');
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();
      return tenantId && planIdValue
        ? `/api/tenants/${tenantId}/plans/${planIdValue}`
        : undefined;
    });
  }

  planRepairsResource(planId: Signal<string>) {
    return httpResource<PlanRepairs>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();

      return tenantId && planIdValue
        ? `/api/tenants/${tenantId}/plans/${planIdValue}/repairs`
        : undefined;
    });
  }

  comparePlansResource(planIds: Signal<string[]>) {
    return httpResource<ComparePlans[]>(
      () => {
        const tenantId = this.#tenantContext.currentTenantId();
        const planIdsValue = planIds();

        if (!tenantId || planIdsValue.length === 0) {
          return undefined;
        }

        const query = planIdsValue
          .map((planId) => `planIds=${encodeURIComponent(planId)}`)
          .join('&');

        return `/api/tenants/${tenantId}/plans/compare?${query}`;
      },
      {
        defaultValue: [],
      },
    );
  }

  planAnnualSpendByRepairCategoryResource(planId: Signal<string>) {
    return httpResource<PlanAnnualSpendYearResponse[]>(() => {
      this.#dataInvalidationService.track([
        'plans:projects',
        'plans:projects:assets',
      ]);
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();
      return tenantId && planIdValue
        ? `/api/tenants/${tenantId}/plans/${planIdValue}/projected-metrics/visualization/annual-spend-by-repair-category`
        : undefined;
    });
  }

  downloadProjectedMetrics(planId: string): Observable<HttpResponse<Blob>> {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.get(
      `api/tenants/${tenantId}/plans/${planId}/projected-metrics/export`,
      { responseType: 'blob', observe: 'response' },
    );
  }

  planProjectAssetTypesResource(planId: Signal<string>) {
    return httpResource<AssetType[]>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();

      return tenantId && planIdValue
        ? `api/tenants/${tenantId}/plans/${planIdValue}/projected-metrics/asset-types`
        : undefined;
    });
  }

  conditionsBeforeAfterRepairsResource(
    planId: Signal<string>,
    assetType: Signal<AssetType | null>,
  ) {
    return httpResource<ConditionsBeforeAfterRepairsResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdValue = planId();
      const assetTypeValue = assetType();

      if (!tenantId || !planIdValue || !assetTypeValue) {
        return undefined;
      }

      return `api/tenants/${tenantId}/plans/${planIdValue}/projected-metrics/visualization/conditions-before-after-repairs/${encodeURIComponent(assetTypeValue)}`;
    });
  }

  comparePlansAssetTypesResource(planIds: Signal<string[]>) {
    return httpResource<AssetType[]>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();

      if (!tenantId || planIdsValue.length === 0) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/asset-types?${query}`;
    });
  }

  /**
   * Every asset type in the plans' networks, funded or not. Used by the
   * network-wide charts, which must offer asset types with nothing funded yet.
   * Accepts one plan id (plan detail) or a comparison set.
   */
  planNetworkAssetTypesResource(planIds: Signal<string[]>) {
    return httpResource<AssetType[]>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();

      if (!tenantId || planIdsValue.length === 0) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/network-asset-types?${query}`;
    });
  }

  comparePlansFundsPlannedVsNotPlannedResource(
    planIds: Signal<string[]>,
    assetType: Signal<AssetType | null>,
  ) {
    return httpResource<ComparePlansFundsPlannedVsNotPlannedResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();
      const assetTypeValue = assetType();

      if (!tenantId || planIdsValue.length === 0 || !assetTypeValue) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/visualization/funds-planned-vs-not-planned?${query}&assetType=${encodeURIComponent(assetTypeValue)}`;
    });
  }

  comparePlansAnnualSpendResource(
    planIds: Signal<string[]>,
    enabled?: Signal<boolean>,
  ) {
    return httpResource<ComparePlansAnnualSpendResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();

      if (!tenantId || planIdsValue.length === 0 || (enabled && !enabled())) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/visualization/annual-spend?${query}`;
    });
  }

  comparePlansSummaryResource(planIds: Signal<string[]>) {
    return httpResource<ComparePlansSummaryResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();

      if (!tenantId || planIdsValue.length === 0) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/summary?${query}`;
    });
  }

  comparePlansConditionDistributionResource(
    planIds: Signal<string[]>,
    assetType: Signal<AssetType | null>,
  ) {
    return httpResource<ComparePlansConditionDistributionResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();
      const assetTypeValue = assetType();

      if (!tenantId || planIdsValue.length === 0 || !assetTypeValue) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/visualization/condition-distribution?${query}&assetType=${encodeURIComponent(assetTypeValue)}`;
    });
  }

  comparePlansSummaryMetricsByAssetTypeResource(
    planIds: Signal<string[]>,
    assetType: Signal<AssetType | null>,
    enabled: Signal<boolean>,
  ) {
    return httpResource<ComparePlansSummaryMetricsByAssetTypeResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();
      const assetTypeValue = assetType();

      if (
        !tenantId ||
        planIdsValue.length === 0 ||
        !assetTypeValue ||
        !enabled()
      ) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/summary/metrics-by-asset-type?${query}&assetType=${encodeURIComponent(assetTypeValue)}`;
    });
  }

  comparePlansVolumeOfRepairsResource(
    planIds: Signal<string[]>,
    assetType: Signal<AssetType | null>,
  ) {
    return httpResource<ComparePlansVolumeOfRepairsResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();
      const assetTypeValue = assetType();

      if (!tenantId || planIdsValue.length === 0 || !assetTypeValue) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/visualization/volume-of-repairs?${query}&assetType=${encodeURIComponent(assetTypeValue)}`;
    });
  }

  comparePlansConditionOverTimeChartResource(
    planIds: Signal<string[]>,
    assetType: Signal<AssetType | null>,
  ) {
    return httpResource<ComparePlansConditionOverTimeChartResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();
      const assetTypeValue = assetType();

      if (!tenantId || planIdsValue.length === 0 || !assetTypeValue) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/visualization/condition-over-time?${query}&assetType=${encodeURIComponent(assetTypeValue)}`;
    });
  }

  comparePlansAnnualSpendBreakdownResource(
    planIds: Signal<string[]>,
    breakdown: Signal<ComparePlansAnnualSpendBreakdownCategory>,
  ) {
    return httpResource<ComparePlansAnnualSpendBreakdownResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const planIdsValue = planIds();
      const breakdownValue = breakdown();

      if (!tenantId || planIdsValue.length === 0 || !breakdownValue) {
        return undefined;
      }

      const query = planIdsValue
        .map((planId) => `planIds=${encodeURIComponent(planId)}`)
        .join('&');

      return `api/tenants/${tenantId}/plans/compare/visualization/annual-spend-breakdown?breakdownCategory=${breakdownValue}&${query}`;
    });
  }
}
